<template>
  <div class="relative col-span-2 space-y-4">
    <BookingRangePicker
      v-if="!hideBookingRangePicker"
      v-model:start-date="startDate"
      v-model:end-date="endDate"
      class="col-span-2"
      :booking-id="props.booking.id"
      :disabled="disabled"
      :start-date-title="t('rentalStart')"
      :end-date-title="t('rentalEnd')"
    />

    <div v-else class="col-span-2 grid w-full grid-cols-2 space-x-2">
      <LabeledText class="px-1" :label="t('rentalStart')">
        {{ formatDate(startDate) }}
      </LabeledText>
      <LabeledText class="px-1" :label="t('rentalEnd')"> {{ formatDate(endDate) }}</LabeledText>
    </div>

    <DisplayLocation v-if="showLocations" :booking="booking" hide-station-addresses />

    <div v-if="hasChanged" class="mt-2">
      <div class="space-x-2">
        <CVButton
          :disabled="isBusy"
          variant="default"
          :is-loading="isBusy"
          @click.prevent="recalculatePriceAndSave"
        >
          {{ t('save') }}
        </CVButton>
        <CVButton :disabled="isBusy" @click.prevent="cancelTimeRangeChange">
          {{ t('cancel') }}
        </CVButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Booking } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import BookingRangePicker from '@/views/handover/components/overview/BookingRangePicker.vue';
import { computed, ref, watch } from 'vue';
import LabeledText from '../LabeledText.vue';
import DisplayLocation from './DisplayLocation.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { formatDate } from '@/hooks/use-formatted-date.ts';
import { CounterpartyType, PriceRecalculationMode } from '@carvia/ros-client-types';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';

const props = defineProps<{
  booking: Booking;
  showLocations?: boolean;
  hideBookingRangePicker?: boolean;
  disabled?: boolean;
  handoverAdjustment?: boolean;
}>();
const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
}>();

const { t } = useI18n();

const startDate = ref(props.booking.startDate);
const endDate = ref(props.booking.endDate);
const { confirmAndSave, isBusy, reset } = usePriceChangeConfirmation();

const hasChanged = computed(
  () => startDate.value !== props.booking.startDate || endDate.value !== props.booking.endDate,
);

const extendTimePeriodOfPricing = async () => {
  const { status } = await confirmAndSave({
    booking: props.booking,
    updateBookingDto: {
      startDate: startDate.value,
      endDate: endDate.value,
      pricing: {
        priceRecalculationMode: props.booking.insuranceCase
          ? PriceRecalculationMode.SCALE
          : PriceRecalculationMode.REBOOK,
      },
    },
    handoverAdjustment: props.handoverAdjustment,
    ...(props.handoverAdjustment ? { priceDifferenceCounterparty: CounterpartyType.CUSTOMER } : {}),
    allowKeepOldPrice: true,
  });
  if (status === 'cancelled') {
    cancelTimeRangeChange();
  }
};

const cancelTimeRangeChange = () => {
  startDate.value = props.booking.startDate;
  endDate.value = props.booking.endDate;
  reset();
};

const recalculatePriceAndSave = async () => {
  await extendTimePeriodOfPricing();
};

watch(hasChanged, (newValue) => {
  emit('hasChanged', newValue);
});
</script>

<i18n lang="json">
{
  "en": {
    "rentalStart": "Rental Start",
    "rentalEnd": "Rental End",
    "recalculatePrice": "Recalculate Price",
    "recalculatingPrice": "Recalculating Price...",
    "newPrice": "New Price",
    "lp": "LP",
    "color": "Color",
    "choose": "Choose",
    "alternativeCarSelected": "Alternative Car selected",
    "vehicleNotAvailableSelectAlternative": "The vehicle is not available in this time period. Please choose an alternative:",
    "noAlternativeVehicleAvailable": "No alternative vehicle available. Please try another time period."
  },
  "de": {
    "rentalStart": "Mietbeginn",
    "rentalEnd": "Mietende",
    "recalculatePrice": "Preis neu berechnen",
    "recalculatingPrice": "Preis wird neu berechnet...",
    "chargeAndSave": "Abbuchen und speichern",
    "newPrice": "Neuer Preis",
    "lp": "Knz",
    "color": "Farbe",
    "choose": "Auswählen",
    "alternativeCarSelected": "Alternativfahrzeug ausgewählt",
    "vehicleNotAvailableSelectAlternative": "Das Fahrzeug ist in dem Zeitraum nicht verfügbar. Bitte wählen Sie eine Alternative:",
    "noAlternativeVehicleAvailable": "Kein Alternativfahrzeug verfügbar. Bitte versuchen Sie einen anderen Zeitraum."
  },
  "es": {
    "rentalStart": "Inicio del alquiler",
    "rentalEnd": "Fin del alquiler",
    "recalculatePrice": "Recalcular precio",
    "recalculatingPrice": "Recalculando el precio…",
    "newPrice": "Nuevo precio",
    "lp": "Matrícula",
    "color": "Color",
    "choose": "Elegir",
    "alternativeCarSelected": "Vehículo alternativo seleccionado",
    "vehicleNotAvailableSelectAlternative": "El vehículo no está disponible en este periodo. Elija una alternativa:",
    "noAlternativeVehicleAvailable": "No hay vehículo alternativo disponible. Pruebe con otro periodo."
  }
}
</i18n>
